import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { fetchMyKpis } from '../store/slices/kpisSlice';
import LoadingSpinner from '../components/LoadingSpinner';

export default function KPIs() {
  const dispatch = useDispatch();
  const { myKpis, loading } = useSelector((state) => state.kpis);

  useEffect(() => {
    dispatch(fetchMyKpis({ period_type: 'monthly' }));
  }, [dispatch]);

  const latestKpi = myKpis[0];

  const chartData = [...myKpis].reverse().map((kpi) => ({
    period: format(new Date(kpi.period_start), 'MMM'),
    distance: parseFloat(kpi.total_distance) || 0,
    trips: kpi.total_trips || 0,
    score: parseFloat(kpi.overall_score) || 0,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">My KPIs</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600">Total Trips</div>
          <div className="text-3xl font-bold text-primary-600">{latestKpi?.total_trips || 0}</div>
          <div className="text-xs text-gray-500">This month</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600">Distance</div>
          <div className="text-3xl font-bold text-primary-600">
            {parseFloat(latestKpi?.total_distance || 0).toFixed(0)}
          </div>
          <div className="text-xs text-gray-500">km this month</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600">Fuel Efficiency</div>
          <div className="text-3xl font-bold text-primary-600">
            {parseFloat(latestKpi?.fuel_efficiency || 0).toFixed(1)}
          </div>
          <div className="text-xs text-gray-500">km/L</div>
        </div>
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-sm text-gray-600">Overall Score</div>
          <div className="text-3xl font-bold text-green-600">
            {parseFloat(latestKpi?.overall_score || 0).toFixed(1)}
          </div>
          <div className="text-xs text-gray-500">out of 100</div>
        </div>
      </div>

      {/* Charts */}
      {chartData.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Distance Chart */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">Monthly Distance</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="distance" fill="#3b82f6" name="Distance (km)" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Score Chart */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold mb-4">Performance Score</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Line type="monotone" dataKey="score" stroke="#22c55e" strokeWidth={2} name="Score" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Detailed Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <h3 className="text-lg font-semibold p-6 border-b">Monthly History</h3>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Trips</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distance</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fuel Eff.</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Score</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {myKpis.map((kpi) => (
              <tr key={kpi.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {format(new Date(kpi.period_start), 'MMMM yyyy')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{kpi.total_trips || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {parseFloat(kpi.total_distance || 0).toFixed(0)} km
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {parseFloat(kpi.total_hours || 0).toFixed(1)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {parseFloat(kpi.fuel_efficiency || 0).toFixed(1)} km/L
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`font-semibold ${parseFloat(kpi.overall_score || 0) >= 70 ? 'text-green-600' : 'text-yellow-600'}`}>
                    {parseFloat(kpi.overall_score || 0).toFixed(1)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
